'use client'
import React from 'react'
import { atom, useAtom } from 'jotai'
import {
    clone,
    compact,
    find,
    filter,
    first,
    remove,
    pick,
    isNumber,
} from 'lodash'

import {
    useAdminCreateProviderDepartmentOptionMutation,
    useAdminPracticeProviderDepartmentOptionsQuery,
    useAdminDeleteProviderDepartmentOptionMutation,
    useAdminUpdateProviderDepartmentOptionMutation,
    useAdminUpsertProviderDepartmentOptionsMutation,
    typedAdminGetCompiledProviderDepartmentOptionQueryDocument,
} from '@docpace/admin-react-apollo'
import {
    ProviderDepartmentOptionFragment,
    ProviderDepartmentOptionNoIdFragment,
} from '@docpace/shared-graphql/fragments'
import {
    ProviderDepartmentOptionItem,
    AppointmentCycleStatsType,
    ApptTypeProviderDepartmentIdInterface,
} from '@docpace/shared-ts-types'
import { 
    filterByApptTypeProviderDepartmentId,
} from '@docpace/shared-ts-types'
import { ctxPracticeIdAtom, ctxSelectedPdoIds } from '@docpace/shared-react-atoms'

const fields = [
    'appointmentTypeId',
    'departmentId',
    'providerId',
    ...Object.keys(ProviderDepartmentOptionItem),
]

interface UseProviderDepartmentOptionsProps {
    practiceId: string
}

export interface UseProviderDepartmentOptionsOutput {
    practiceDefaultOption: ProviderDepartmentOptionFragment | null
    appointmentTypeDefaultOption: (appointmentTypeId: string)=>ProviderDepartmentOptionFragment | null
    departmentDefaultOption: (departmentId: string)=>ProviderDepartmentOptionFragment | null
    providerDefaultOption: (providerId: string)=>ProviderDepartmentOptionFragment | null
    providerDepartmentOptions: ProviderDepartmentOptionFragment[]
    setOption: (
        ids: ApptTypeProviderDepartmentIdInterface,
        optKey: ProviderDepartmentOptionItem,
        value?: any
    ) => void
    getOption: (
        ids: ApptTypeProviderDepartmentIdInterface
    ) => ProviderDepartmentOptionNoIdFragment | null
    getModifiedOption: (
        ids: ApptTypeProviderDepartmentIdInterface
    ) => ProviderDepartmentOptionNoIdFragment | null
    // Queued value if the option has pending changes, otherwise the saved one
    getLatestOption: (
        ids: ApptTypeProviderDepartmentIdInterface
    ) => ProviderDepartmentOptionNoIdFragment | null
    getProviderDepartmentOptionByIds: (
        ids: ApptTypeProviderDepartmentIdInterface
    ) => ProviderDepartmentOptionFragment | null
    isLoading: boolean
    handleSubmit: () => Promise<void>
    handleCancel: () => Promise<void>
    refetchProviderDepartmentOptions: () => Promise<any>
    isSubmitting: boolean
    hasModifications: boolean
    updateProviderDepartmentOption: (
        options?: any
    ) => Promise<any>
    createProviderDepartmentOption: (
        options?: any
    ) => Promise<any>
    deleteProviderDepartmentOption: (
        options?: any
    ) => Promise<any>
    upsertProviderDepartmentOptions: (
        options?: any
    ) => Promise<any>
}

const getOptByIds: (
    providerDepartmentOptions: ProviderDepartmentOptionFragment[],
    ids: ApptTypeProviderDepartmentIdInterface
) => ProviderDepartmentOptionFragment = (providerDepartmentOptions, ids) => {
    return providerDepartmentOptions?.filter((opt) =>
        filterByApptTypeProviderDepartmentId(opt, ids)
    )?.[0]
}

// Queue state is shared across all hook instances so changes queued from the
// PDO form dialog can be submitted/cancelled from the page-level buttons
const pdoOptionsToAddAtom = atom<ProviderDepartmentOptionNoIdFragment[]>([])
const pdoOptionsToUpdateAtom = atom<ProviderDepartmentOptionFragment[]>([])
const pdoIsSubmittingAtom = atom<boolean>(false)

export const useProviderDepartmentOptions: (
    props?: UseProviderDepartmentOptionsProps
) => UseProviderDepartmentOptionsOutput = (props) => {
    const [optionsToAdd, setOptionsToAdd] = useAtom(pdoOptionsToAddAtom)
    const [optionsToUpdate, setOptionsToUpdate] = useAtom(pdoOptionsToUpdateAtom)
    const [ ctxPracticeId ] = useAtom(ctxPracticeIdAtom)
    const [isSubmitting, setIsSubmitting] = useAtom(pdoIsSubmittingAtom)
    const [selectedPdoIds] = useAtom(ctxSelectedPdoIds)
    const practiceId = props?.practiceId ?? ctxPracticeId ?? ''
    const { data, loading, refetch } =
        useAdminPracticeProviderDepartmentOptionsQuery({
            variables: { practiceId },
            skip: !practiceId,
        })

    const refetchQueries = [{ 
        query: typedAdminGetCompiledProviderDepartmentOptionQueryDocument, 
        variables: { ...selectedPdoIds, practiceId }
    }]

    const [deleteProviderDepartmentOption] =
        useAdminDeleteProviderDepartmentOptionMutation({ refetchQueries })
    const [createProviderDepartmentOption] =
        useAdminCreateProviderDepartmentOptionMutation({ refetchQueries })
    const [updateProviderDepartmentOption] =
        useAdminUpdateProviderDepartmentOptionMutation({ refetchQueries })
    const [upsertProviderDepartmentOptions] =
        useAdminUpsertProviderDepartmentOptionsMutation({ refetchQueries })

    const providerDepartmentOptions: any[] = compact(
        data?.providerDepartmentOptions?.nodes
    )

    const practiceDefaultOption =
        find(providerDepartmentOptions, {
            appointmentTypeId: null,
            departmentId: null,
            providerId: null,
            practiceId
        }) ?? null

    const getOption: (
        ids: ApptTypeProviderDepartmentIdInterface
    ) => ProviderDepartmentOptionFragment | null = (ids) => {
        const res =
            filter(providerDepartmentOptions, ids).filter(
                (r) => !isNumber(r)
            )?.[0] ?? null
        return {
            practiceId,
            providerDepartmentOptionId: -1,
            statsType: AppointmentCycleStatsType.PRACTICE,
            ...(res ?? {}),
        }
    }

    // Fill in the absent id dimensions with explicit nulls so queue and
    // server rows match exactly (e.g. a { providerId } entry must not match
    // a { providerId, departmentId } one)
    const normalizeIds = (
        ids: ApptTypeProviderDepartmentIdInterface
    ): ApptTypeProviderDepartmentIdInterface & { practiceId: string } => ({
        providerId: null,
        departmentId: null,
        appointmentTypeId: null,
        ...ids,
        practiceId: ids?.practiceId ?? practiceId,
    })

    const getModifiedOption = (ids: ApptTypeProviderDepartmentIdInterface) => {
        const normalizedIds = normalizeIds(ids)
        const res =
            first(
                compact([
                    find(optionsToAdd, normalizedIds),
                    find(optionsToUpdate, normalizedIds),
                ])
            ) ?? null
        return !res || isNumber(res) ? null : { ...res, practiceId }
    }

    const getLatestOption: (
        ids: ApptTypeProviderDepartmentIdInterface
    ) => ProviderDepartmentOptionNoIdFragment | null = (ids) => {
        const normalizedIds = normalizeIds(ids)
        const res = first(
            compact([
                find(optionsToAdd, normalizedIds),
                find(optionsToUpdate, normalizedIds),
                find(providerDepartmentOptions, normalizedIds),
            ])
        )

        return res && !isNumber(res) ? {
            statsType: res?.['statsType'],
            ...res,
            practiceId,
        } : null
    }


    const setOption = (
        ids: ApptTypeProviderDepartmentIdInterface,
        optKey: ProviderDepartmentOptionItem,
        value = undefined
    ) => {
        if (isSubmitting) return
        const normalizedIds = normalizeIds(ids)
        const existingOption = getOptByIds(
            providerDepartmentOptions,
            normalizedIds
        )
        const thisLatestOpts = getLatestOption(normalizedIds)
        const thisLatestOpt = thisLatestOpts?.[optKey]
        // With an explicit value, queue it as-is; otherwise cycle the
        // tristate true -> false -> null -> true
        let newOpt: any = value
        if (value === undefined) {
            newOpt = thisLatestOpt === false ? null : !thisLatestOpt
        }
        if (existingOption) {
            const opts = clone(optionsToUpdate)
            remove(opts, normalizedIds)
            setOptionsToUpdate([
                ...compact(opts),
                {
                    providerDepartmentOptionId: existingOption?.providerDepartmentOptionId,
                    statsType: AppointmentCycleStatsType.PRACTICE,
                    ...thisLatestOpts,
                    ...normalizedIds,
                    [optKey]: newOpt,
                },
            ])
        } else {
            const opts = clone(optionsToAdd)
            remove(opts, normalizedIds)
            setOptionsToAdd([
                ...opts.map((r) => ({ ...r, practiceId })),
                {
                    statsType: AppointmentCycleStatsType.PRACTICE,
                    ...thisLatestOpts,
                    ...normalizedIds,
                    [optKey]: newOpt,
                },
            ])
        }
    }

    const reset = async () => {
        setOptionsToAdd([])
        setOptionsToUpdate([])
    }

    const handleCancel = async () => {
        await reset()
    }

    const handleSubmit = async () => {
        if (isSubmitting) return
        setIsSubmitting(true)
        try {
            const optionsToUpsert = [...optionsToAdd, ...optionsToUpdate].map(
                (opt) => ({
                    practiceId,
                    ...pick(opt, fields),
                })
            )
            if (practiceId && optionsToUpsert.length > 0) {
                await upsertProviderDepartmentOptions({
                    variables: {
                        input: {
                            options: optionsToUpsert,
                        },
                    },
                })
            }

            await refetch()
            await reset()
        } finally {
            setIsSubmitting(false)
        }
    }

    const globalProviderDepartmentOption = getOptByIds(
        providerDepartmentOptions,
        {
            practiceId: null,
            appointmentTypeId: null,
            providerId: null,
            departmentId: null,
        }
    )?.[0]

    return {
        setOption,
        isLoading: loading || isSubmitting,
        practiceDefaultOption:
            practiceDefaultOption && !isNumber(practiceDefaultOption)
                ? practiceDefaultOption
                : null,
        providerDefaultOption: (providerId)=>find(providerDepartmentOptions, { practiceId, providerId, departmentId: null, appointmentTypeId: null }),
        appointmentTypeDefaultOption: (appointmentTypeId)=>find(providerDepartmentOptions, { practiceId, providerId: null, departmentId: null, appointmentTypeId }),
        departmentDefaultOption: (departmentId)=>find(providerDepartmentOptions, { practiceId, providerId: null, departmentId, appointmentTypeId: null }),
        providerDepartmentOptions,
        getProviderDepartmentOptionByIds: (
            ids: ApptTypeProviderDepartmentIdInterface
        ) => {
            if (ids === null) return globalProviderDepartmentOption
            const { providerId = null, departmentId = null, appointmentTypeId = null, practiceId } =
                ids
            return getOptByIds(providerDepartmentOptions, {
                practiceId,
                appointmentTypeId,
                providerId,
                departmentId,
            })
        },
        getModifiedOption,
        getLatestOption,
        getOption,
        handleSubmit,
        handleCancel,
        isSubmitting,
        deleteProviderDepartmentOption,
        createProviderDepartmentOption,
        updateProviderDepartmentOption,
        upsertProviderDepartmentOptions,
        hasModifications:
        optionsToAdd?.length > 0 || optionsToUpdate?.length > 0,
        refetchProviderDepartmentOptions: refetch,
    }
}

export default useProviderDepartmentOptions
